import { useMutation } from "@tanstack/react-query";
import {
  UserResponseModel,
  CreateUserModel,
  createUserSchema,
  UserLoginModel,
  userLoginSchema,
  UserModel,
} from "../../data/model/auth-model";
import axios from "@/core/utils/axios-instance";
import showToast from "@/core/utils/show-toast";
import errorMessages from "@/core/utils/error-messages";
import { useAuthContext } from "../../context/auth-context";

/**
 * Generic mutation function for authentication actions
 * @param actionType Type of auth action ('login' or 'register')
 * @param schema Schema for validation
 */
const useAuthMutation = <T>(actionType: "login" | "register", schema: any) => {
  const { login } = useAuthContext();

  return useMutation<UserResponseModel, Error, T>({
    mutationFn: async (credentials: T): Promise<UserResponseModel> => {
      const validateData = schema.parse(credentials);
      const res = await axios.post<UserResponseModel>(
        `/auth/${actionType}`,
        validateData
      );
      const response = res.data;
      return response;
    },
    onSuccess: (data: UserResponseModel) => {
      showToast(data.message, "success");
      console.log("Login successful, received data:", data);
      // Use the context login function instead of directly setting localStorage
      const user = data.data.user as UserModel;
      const token = (data.data.accessToken || data.data.token) as string;
      const refreshToken = (data.data.refreshToken || "") as string;
      
      if (!refreshToken) {
        console.warn("No refresh token received in login response!");
      }
      
      login(user, token, refreshToken);
    },
    onError: (error) => {
      showToast(errorMessages(error), "error");
    },
  });
};

/**
 * Hook for user login functionality
 */
export const useLogin = () => {
  return useAuthMutation<UserLoginModel>("login", userLoginSchema);
};

/**
 * Hook for user registration functionality
 */
export const useRegister = () => {
  return useAuthMutation<CreateUserModel>("register", createUserSchema);
};

/**
 * Hook for user logout functionality
 */
export const useLogout = () => {
  const { logout } = useAuthContext();
  
  return useMutation({
    mutationFn: async () => {
      await axios.post('/auth/logout');
    },
    onSuccess: () => {
      logout();
      showToast("Logged out successfully", "success");
      window.location.href = "/auth/login";
    },
    onError: (error) => {
      console.error("Logout failed", error);
      // Even if API fails, we should logout locally
      logout();
      window.location.href = "/auth/login";
    }
  });
};

